import type { ChangeEventHandler, Dispatch, FC, SetStateAction } from 'react'

import { OnlyFansList } from '@api'
import debounce from 'lodash/debounce'
import { InfiniteData, useQueryClient } from 'react-query'

import CheckIcon from '@/assets/icons/icon-check.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import PlusIcon from '@/assets/icons/icon-plus.svg'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { TextField } from '@/components/ui/text-field'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs'
import type {
  GetListsResponse,
  ListOrderOptions,
  ListSortOptions,
} from '@/modules/chatting/types'
import type {
  listOrderOptions,
  listSortOptions,
} from '@/modules/chatting/ui/chat-window/lists/consts'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'

export interface ListsRequestConfig {
  sort: ListSortOptions
  order: ListOrderOptions
  setSort: (value: ListSortOptions) => void
  setOrder: (value: ListOrderOptions) => void
  sortOptions: typeof listSortOptions
  orderOptions: typeof listOrderOptions
  query: string
  setQuery: Dispatch<SetStateAction<string>>
  isSortAndOrderDisabled: boolean
}

type Props = ListsRequestConfig & {
  isCustomizeOrderModeActive: boolean
  onCustomOrderModeEnable: () => void
  onCustomOrderModeDisable: () => void
  saveCustomOrderChanges: () => Promise<void>
  isCustomOrderChangeLoading: boolean
}

export const ListHeading: FC<Props> = ({
  isCustomizeOrderModeActive,
  onCustomOrderModeDisable,
  onCustomOrderModeEnable,
  saveCustomOrderChanges,
  sort,
  order,
  setSort,
  setOrder,
  sortOptions,
  orderOptions,
  query,
  setQuery,
  isCustomOrderChangeLoading,
  isSortAndOrderDisabled,
}: Props) => {
  const queryClient = useQueryClient()
  const handleCreateList = (list: OnlyFansList) => {
    queryClient.setQueriesData<InfiniteData<GetListsResponse> | undefined>(
      'all-lists-extended',
      (oldData) => {
        if (!oldData) return oldData

        const lastPage = oldData.pages[oldData.pages.length - 1]
        const newLastPage = {
          ...lastPage,
          list: [...lastPage.list, list],
        }

        return {
          ...oldData,
          pages: [...oldData.pages.slice(0, -1), newLastPage],
        }
      },
    )
  }

  const handleClickCreate = () => {
    openDialog({
      type: DIALOG_TYPE.CREATE_LIST,
      onSuccess: handleCreateList,
    })
  }
  const handleSaveCustomOrderChanges = () => {
    saveCustomOrderChanges().then(() => {
      onCustomOrderModeDisable()
    })
  }

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = debounce(
    (event) => {
      setQuery(event.target.value)
    },
    500,
  )

  const customizeAction = (
    <>
      <div className="w-full border-b" />
      <button
        className="hover:bg-secondary-50 dark:hover:bg-secondary-600 mt-1 w-full rounded px-3 py-1.5 text-left text-sm font-medium"
        onClick={onCustomOrderModeEnable}
      >
        Customize Order
      </button>
    </>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-xl font-semibold">Collections</h3>
        <div className="flex items-center gap-3">
          <TextField
            placeholder="Search"
            className="dark:border-secondary-400 flex-1 resize-none"
            prefix={<SearchIcon className="text-title" />}
            defaultValue={query}
            onChange={handleSearchChange}
          />
          <IconButton
            icon={PlusIcon}
            onClick={handleClickCreate}
            tooltip="Create New List"
            iconClassName="h-6 w-6"
          />
        </div>
      </div>
      <p className="text-lg font-semibold">User Lists</p>
      <div className="flex items-center justify-between gap-3">
        {isCustomizeOrderModeActive ? (
          <>
            <IconButton
              rounded
              icon={CloseIcon}
              onClick={onCustomOrderModeDisable}
              className="h-9 w-9"
            />
            <p className="flex-1 font-medium leading-[26px]">Customize Order</p>
            <IconButton
              className="bg-primary-50 text-primary-500 hover:bg-primary-500 dark:bg-primary-900 dark:hover:bg-primary-500 h-9 w-9 hover:text-white"
              icon={CheckIcon}
              onClick={handleSaveCustomOrderChanges}
              disabled={isCustomOrderChangeLoading}
              rounded
            />
          </>
        ) : (
          <>
            <p className="text-secondary-300 font-medium">
              {sort.toUpperCase()}
            </p>
            <FilterSortOrderOptions
              sort={sort}
              order={order}
              setSort={setSort}
              setOrder={setOrder}
              sortOptions={sortOptions}
              orderOptions={orderOptions}
              triggerIconClassName="text-secondary-300"
              extra={customizeAction}
              disabled={isSortAndOrderDisabled}
            />
          </>
        )}
      </div>
    </div>
  )
}
